<template>
    <div>
        <CCol sm="12">
            <CCard>
                <CCardBody class="d-flex flex-column">
                    <div class="d-flex mb-2 justify-content-end align-items-start">
                        <!-- Title -->
                        <p class="flex-grow-1">{{ $t('service') }}</p>
                        <!-- Language dropdown -->
                        <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                    </div>
                    <!-- Form -->
                    <div class="row">
                        <div class="col-12 col-lg-5">
                            <!-- Image -->
                            <div role="group" class="form-group">
                                <label class=""> {{ $t('imageMandatory') }}</label>
                                <GalleryPicker class="gallery-picker" v-model="service.image" :ratioWidth="1"
                                    :ratioHeight="1"
                                    v-bind:class="{ 'is-invalid': genericValidator(service.image, attemptedSubmit) === false || errorValidator('Image') === false }"
                                    :max="1"></GalleryPicker>
                            </div>
                        </div>
                        <div class="col-12 col-lg-7">
                            <div class="row">
                                <div class="col-12">
                                    <!-- Titles -->
                                    <div role="group" class="form-group">
                                        <label class=""> {{ $t('titlesMandatory') }}</label>
                                        <CInput type="text" v-model="service.titles[currentLanguage.key]"
                                            :invalid-feedback="errorOutput['Titles']"
                                            :is-valid="genericValidator(service.titles, attemptedSubmit) && errorValidator('Titles')">
                                        </CInput>
                                    </div>
                                    <!-- Descriptions -->
                                    <div role="group" class="form-group">
                                        <label class=""> {{ $t('descriptionsMandatory') }}</label>
                                        <vue-editor v-model="service.descriptions[currentLanguage.key]"
                                            v-bind:class="{ 'is-invalid': genericValidator(service.descriptions, attemptedSubmit) === false || errorValidator('Descriptions') }"></vue-editor>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Action button + submitting -->
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary col-2 align-self-end" @click="submitClicked">{{
                        $t('acceptChanges') }}</CButton>
                    <!-- Loading -->
                    <FormLoading v-if="loading"></FormLoading>
                </CCardBody>
            </CCard>
        </CCol>
        <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup"
            v-model="showConfirmationPopup"></ConfirmationPopUp>
    </div>
</template>
 
<script>
import { mapState } from 'vuex'
import router from '@/router/index';
import { API_HELPER } from "@/helpers/api.js";
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";
import Service from "@/classes/Service";
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'

export default {
    components: { LanguageDropdown, ConfirmationPopUp, VueEditor, GalleryPicker, FormLoading },
    props: {
        baseService:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            service: new Service({}, {}),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
        }
    },
    mounted: function () {
        this.resetService();
    },
    computed:
    {
        ...mapState('services', ['putServiceOutput']),
        ...mapState('services', ['postServiceOutput']),
        ...mapState({
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function () {
            var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate multi language fields
            for (const [key, value] of Object.entries(this.service.titles)) { if (!value) { result['Titles'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.service.descriptions)) { if (!value) { result['Descriptions'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        name: function () {
            return this.baseService || this.baseName ? (this.baseService ? this.baseService.name : this.baseName) : null;
        }
    },
    methods: {
        genericValidator,
        errorValidator,
        resetService() {
            if (!this.baseService) {
                var titles = {}; var descriptions = {};
                CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = ""; descriptions[language.key] = ""; });
                this.service = new Service(titles, descriptions);
            }
            else {
                this.service = deepClone(new Service, this.baseService);
                this.service.image = this.service.image ? { url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.service.image]) } : null;
            }
        },
        submitClicked() {
            this.attemptedSubmit = true;

            // Emit event
            if (this.service.isValid()) {
                this.submitting = true;
                this.$emit('submit-requested', this.service);
            }
        },
        onClickConfirmationPopup(value) {
            this.showConfirmationPopup = value;
            router.push({ name: 'ServicesList' });
        },
        onProcessTask: function (val) {
            if (val != null) {
                if (val) {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        }
    },
    watch:
    {
        putServiceOutput: function (val) {
            this.onProcessTask(val);
        },
        postServiceOutput: function (val) {
            this.onProcessTask(val);
        },
        baseService: function () {
            this.resetService();
        },
        alertOutput: function () {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if (typeof this.alertOutput === 'string' || this.alertOutput instanceof String) {
                alert(this.alertOutput);
            }
        }
    }
}
</script> 
 
<style scoped>
@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

/deep/.quillWrapper.is-invalid .ql-snow.ql-toolbar,
/deep/.quillWrapper.is-invalid .ql-snow.ql-container {
    border-color: #e55353;
}
</style> 